"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useEnvContext } from "next-runtime-env";
import { usePathname } from "next/navigation";
import { useCallback } from "react";

import { getCatalogInfiniteProductsQueryOptions } from "../api/catalog.query";
import { useCatalogBlockContext } from "../components/CatalogPage/CatalogBlockContext";
import { getCatalogHeaders } from "../server/catalogHeaders.helper";
import type { ActiveCatalogFilters } from "../types/filters.type";
import { buildSearchPathForBff } from "../types/searchTerm.helper";
import { useCatalogSettings } from "./useCatalogSettings.hook";
import { useFilterParams } from "./useFilterParams.hook";

type Props = {
  enabled: boolean;
};

export function useInfiniteCatalogResults({ enabled }: Props) {
  const { NEXT_PUBLIC_BFF_API } = useEnvContext();
  const pathname = usePathname();
  const locale = useLocale();
  const catalogBlockContext = useCatalogBlockContext();
  const [settings, setSettings] = useCatalogSettings();
  const [filterParams] = useFilterParams();

  const activeFilters = Object.fromEntries(
    Object.entries(filterParams).filter(([, v]) => v !== null),
  ) as ActiveCatalogFilters;

  const { queryKey, queryFn, retry, staleTime } =
    getCatalogInfiniteProductsQueryOptions({
      apiOrigin: NEXT_PUBLIC_BFF_API!,
      path: buildSearchPathForBff(catalogBlockContext.path || pathname),
      isCMSPath: catalogBlockContext.isCMS,
      sort: settings.sort,
      extraHeaders: getCatalogHeaders(),
      filters:
        Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
      locale,
    });

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.total_pages
        ? lastPage.pagination.page + 1
        : undefined,
    retry,
    staleTime,
    enabled,
  });

  const lastPage = query.data?.pages.at(-1);
  const products = query.data?.pages.flatMap((p) => p.products) ?? [];
  const totalProducts = lastPage?.pagination.total ?? 0;
  const totalPages = lastPage?.pagination.total_pages ?? 0;
  const isLoading = query.isLoading;
  const isLastPage = !query.hasNextPage && !query.isLoading && totalPages > 0;

  const { fetchNextPage } = query;

  const loadMoreProducts = useCallback(async () => {
    const result = await fetchNextPage();
    const newPage = result.data?.pages.at(-1)?.pagination?.page;
    if (newPage !== undefined && newPage > 1) {
      setSettings({ page: newPage });
    }
  }, [fetchNextPage, setSettings]);

  return {
    query,
    products,
    totalProducts,
    isLoading,
    isLastPage,
    totalPages,
    loadMoreProducts,
    isFetchingMore: query.isFetchingNextPage,
  };
}
